import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { formatNumberOfPages, formatPrice } from 'utils/format';
import { useRouter } from 'next/router';
import useModalContext from 'components/context/modal';
import Tooltip from 'components/rich/tooltip';
import useResizeObserver from '@react-hook/resize-observer';
import { getMaximumNumberOfPages } from 'editor-utils/template';

import { Layout, BreadCrumb } from '../../components/rich';
import { Button, Logo, Typography } from '../../components/base';
import useEditorItemContext from '../../context/editorItemContext';
import round from 'utils/round';

const EditorLayout = props => {
  const editorItem = useEditorItemContext();

  let template;
  let product;
  if (editorItem) {
    template = editorItem.template;
    product = editorItem.product;
  } else {
    product = props.product;
  }

  const router = useRouter();
  const modalContext = useModalContext();

  const handleOnLogoClick = e => {
    e.preventDefault();

    modalContext.confirm(
      'Are you sure you want to leave your project now? Your changes are automatically saved as a draft if you want to resume editing later.',
      {
        title: 'Back to Home?',
        cancelLabel: 'Stay on this page',
        confirmLabel: 'Leave this page',
        onConfirm: () => {
          if (product.url) {
            router.push('/[...params]', product.url);
          } else {
            router.push('/');
          }
        },
      },
    );
  };

  //
  // BREADCRUMB
  //
  const breadCrumbItems = [
    { label: product.name },
    ...props.breadCrumbItems.map(item => ({
      ...item,
    })),
  ];

  //
  // PRICE vs NUMBER OF PAGES
  //
  let price;
  if (props.numberOfPages) {
    const maximumNumberOfPages = getMaximumNumberOfPages(template, false);
    price = (
      <Tooltip text={`Maximum number of pages is ${maximumNumberOfPages}`}>
        <span style={props.numberOfPages > maximumNumberOfPages ? { color: 'red' } : {}}>
          {formatNumberOfPages(props.numberOfPages)}
        </span>
      </Tooltip>
    );
  } else if (product.prices) {
    price = `Starting at ${formatPrice(product.prices[0], product.currency, true)}`;
  } else if (props.price != null || product.price != null) {
    price = formatPrice(props.price || product.price, product.currency, true);
  }

  const viewPortSizeRef = useRef([]);

  const ref = useRef();
  useResizeObserver(ref, val => {
    if (val?.contentRect?.width != null && val?.contentRect?.height != null) {
      const value = [round(val.contentRect.width, 0), round(val.contentRect.height, 0)];

      if (value.join(':') !== viewPortSizeRef.current.join(':')) {
        if (props.onViewPortSizeChange) {
          props.onViewPortSizeChange(value);
        }
        viewPortSizeRef.current = value;
      }
    }
  });

  //
  // CHILDREN
  //
  let children;
  if (props.toolbars.length > 0) {
    children = (
      <Layout.Container noPadding={props.noPadding} ltr>
        <Layout.Aside>{props.toolbars}</Layout.Aside>
        <Layout.Content centeredContent={props.centeredContent}>{props.children}</Layout.Content>
      </Layout.Container>
    );
  } else {
    children = (
      <Layout.Container
        noPadding={props.noPadding}
        style={
          props.centerVertical && {
            minHeight: '100%',
            alignItems: 'center',
            display: 'flex',
            margin: 0,
          }
        }
      >
        <Layout.Content centeredContent={props.centeredContent}>{props.children}</Layout.Content>
      </Layout.Container>
    );
  }

  return (
    <Layout className={props.className}>
      <Layout.Header>
        <Layout.Container display={['flex']} style={{ justifyContent: 'space-between' }}>
          <Layout.HeaderLeft>
            <Button p={0} onClick={handleOnLogoClick}>
              <Logo />
            </Button>
          </Layout.HeaderLeft>
          <BreadCrumb fluid={props.breadCrumbFluid}>
            {breadCrumbItems.map(item => (
              <BreadCrumb.Item key={item.label}>
                {item.url ? (
                  <Typography.Link href={item.url} onClick={e => item.onClick(e, item.url)}>
                    {item.label}
                  </Typography.Link>
                ) : (
                  item.label
                )}
              </BreadCrumb.Item>
            ))}
          </BreadCrumb>
          <Layout.HeaderRight>
            {props.actionBar}
            {props.showPrice && price != null && (
              <Layout.HeaderTitle color="primary" borderColor="primary">
                {price}
              </Layout.HeaderTitle>
            )}
            {props.actionButton}
          </Layout.HeaderRight>
        </Layout.Container>
      </Layout.Header>
      <Layout.Main ref={ref} id="editor-container">
        {props.primaryChildren && (
          <Layout.Container mt={3} mb={3}>
            {props.primaryChildren}
          </Layout.Container>
        )}
        {children}
        {props.controls}
      </Layout.Main>
      {props.footerChildren && (
        <Layout.Footer className="bg-white p-0">
          <div
            className="d-flex position-relative"
            style={{ boxShadow: `0 0 0 1px rgb(17 20 24 / 10%), 0 1px 1px rgb(17 20 24 / 20%)` }}
          >
            {props.footerChildren}
          </div>
        </Layout.Footer>
      )}
    </Layout>
  );
};

EditorLayout.propTypes = {
  product: PropTypes.object,
  breadCrumbFluid: PropTypes.bool,
  breadCrumbItems: PropTypes.arrayOf(
    PropTypes.shape({ url: PropTypes.string, label: PropTypes.string.isRequired }),
  ),
  actionBar: PropTypes.node,
  numberOfPages: PropTypes.number,
  price: PropTypes.number,
  showPrice: PropTypes.bool,
  actionButton: PropTypes.node,
  toolbars: PropTypes.arrayOf(PropTypes.node),
  primaryChildren: PropTypes.node,
  footerChildren: PropTypes.node,
  controls: PropTypes.node,
  centeredContent: PropTypes.bool,
  noPadding: PropTypes.bool,
  children: PropTypes.node,
  onViewPortSizeChange: PropTypes.func,
};

EditorLayout.defaultProps = {
  product: {},
  breadCrumbFluid: false,
  breadCrumbItems: [],
  showPrice: true,
  toolbars: [],
  centeredContent: false,
  noPadding: false,
};

export default EditorLayout;
